<template>
  <div class="custom-border-outline mt-3">
    <div class="d-flex justify-content-between flex-wrap align-items-center">
      <h5 class="text-evollu-gray-light is-bold mb-0 pl-3 py-3">
        {{$t('iframe.titles.media_details')}}
      </h5>

      <i
        v-if="collapsable"
        class="clickable p-3"
        :class="{'fa fa-chevron-up': !isCollapsed, 'fa fa-chevron-down': isCollapsed}"
        @click="isCollapsed = !isCollapsed"
      >
      </i>
    </div>
    <div v-if="!isCollapsed">
      <form class="evollu-form">
        <div class="row col-12">
          <div class="row col-md-6">
            <div class="col-12">
              <label class="form-label">{{ $t('iframe.table_headings.client') }}</label>
              <multiselect
                :options="clientOptions"
                id="client"
                v-model="client"
                label="name"
                track-by="name"
                :disabled="clientOptions.length === 1"
                select-label=""
                selected-label=""
                deselect-label=""
                placeholder="---"
                open-direction="bottom"
                :allow-empty="false"
                :multiple="false"
                :searchable="true"
              >
                <span slot="noResult">{{ $t('patients.noResults') }}</span>
              </multiselect>
            </div>

          </div>
        </div>
        <div class="row col-12" style="margin-bottom: 0 !important;">
          <!-- input name -->
          <div class="row col-md-6">
            <div class="col-12">
              <va-input
                id="name"
                v-model="value.name"
                :label="$t('iframe.media_library.item_name')"
                :placeholder="$t('iframe.media_library.item_name')"
                @input="formErrors.clear('name')"
              />

              <small v-show="formErrors.has('name')" class="help text-danger">
                {{ $t(formErrors.get('name')) }}
              </small>
            </div>
          </div>
          <!-- --- --->

          <!-- lang -->
          <div class="row col-md-6">
            <div class="col-12">
              <label class="form-label">{{ $t('iframe.table_headings.lang') }}</label>
              <multiselect
                :options="langOptions"
                id="lang"
                v-model="value.lang"
                :custom-label="opt => opt.language_key ? `${opt.emoji} ${$t(`language.${opt.language_key}`)}` : 'N/A'"
                track-by="lang"
                select-label=""
                selected-label=""
                deselect-label=""
                placeholder="---"
                open-direction="bottom"
                :allow-empty="false"
                :multiple="false"
                :searchable="true"
                @input="formErrors.clear('lang')"
              >
                <span slot="noResult">{{ $t('patients.noResults') }}</span>
              </multiselect>

              <small v-show="formErrors.has('lang')" class="help text-danger">
                {{ $t(formErrors.get('lang')) }}
              </small>
            </div>
          </div>
          <!-- --- --->
        </div>

        <div class="row col-12">
          <!-- Upload Image -->
          <div class="col-md-6 mb-5" style="height: 8vh">
            <label class="pl-3 form-label mb-0 pt-2">{{ $t('iframe.media_library.item_image') }}</label>
            <div class="upload-div h-100">
              <div v-if="!value.icon" class="d-flex justify-content-start align-items-center col-12"  @click="uploadImage" >
                <div class="d-flex justify-content-center w-100">
                  <span class="clickable d-flex w-50 pl-4 justify-content-end">
                    <span class="d-flex align-items-center mr-3" >
                      <i class="i-add-bg add-icon-wh"></i>
                    </span>
                    <span class="d-flex flex-column align-items-center justify-content-center">
                      <span class="upload_input" style="color: #171725">{{$t('iframe.messages.drag_and_drop_file_or')}}</span>
                      <span style="color: #8f76db">{{$t('iframe.messages.browser_computer')}}</span>
                    </span>
                  </span>
                  <span class="verical-line"></span>
                  <span class="clickable d-flex w-50 pr-4 justify-content-start">
                    <span class="d-flex flex-column justify-content-center">
                      <span style="color: #8E99B4">{{$t('iframe.messages.maximum_size')}}: 5MB </span>
                      <span style="color: #8E99B4">{{$t('iframe.messages.supported_formats')}}: svg, png</span>
                    </span>
                  </span>
                </div>
              </div>
              <br/>
              <div class="d-flex justify-content-center col-12" v-if="!value.icon">
                <input ref="uploadIconFileInput" id="upload-file-input" type="file" @input="onIconFileSelected" hidden accept=".svg, .png" >
              </div>
              <div class="d-flex justify-content-center align-items-center col-12" v-else>
                <img :src="handleImgSrc()" width="100px">
                <i class="fa fa-times ml-1 clickable" @click.prevent="removeIcon"></i>
              </div>
              <small v-if="formErrors.has('icon')" class="help text-danger">
                {{$t(formErrors.get('icon'))}}
              </small>
            </div>

          </div>
          <!-- --- --->

          <!-- Upload Sound -->
          <div class="col-md-6 mb-5" style="height: 8vh">
            <label class="pl-3 form-label mb-0 pt-2">{{ $t('iframe.media_library.item_sound') }}</label>
            <div class="upload-div h-100">
              <div v-if="!value.sound" class="d-flex justify-content-start align-items-center col-12"  @click="uploadSound" >
                <div class="d-flex justify-content-center w-100">
                  <span class="clickable d-flex w-50 pl-4 justify-content-end">
                    <span class="d-flex align-items-center mr-3" >
                      <i class="i-add-bg add-icon-wh"></i>
                    </span>
                    <span class="d-flex flex-column align-items-center justify-content-center">
                      <span class="upload_input" style="color: #171725">{{$t('iframe.messages.drag_and_drop_file_or')}}</span>
                      <span style="color: #8f76db">{{$t('iframe.messages.browser_computer')}}</span>
                    </span>
                  </span>
                  <span class="verical-line"></span>
                  <span class="clickable d-flex w-50 pr-4 justify-content-start">
                    <span class="d-flex flex-column justify-content-center">
                      <span style="color: #8E99B4">{{$t('iframe.messages.maximum_size')}}: 5MB </span>
                      <span style="color: #8E99B4">{{$t('iframe.messages.supported_formats')}}: mp3</span>
                    </span>
                  </span>
                </div>
              </div>
              <br/>
              <div class="d-flex justify-content-center col-12" v-if="!value.sound">
                <input ref="uploadSoundFileInput" id="upload-file-input-sound" type="file" @input="onSoundFileSelected" hidden accept=".mp3" >
              </div>
              <div class="d-flex justify-content-center align-items-center col-12" v-else>
                <div class="col-md-2 w-100 d-flex justify-content-center">
                  <div class="btn btn__round btn_add">
                    <va-button
                      :disabled="!value.sound"
                      @click.prevent="playSound"
                    >
                      <i class="fa fa-play"></i>
                    </va-button>
                  </div>
                </div>
                <i class="fa fa-times ml-1 clickable" @click.prevent="removeSound"></i>
              </div>
              <small v-if="formErrors.has('sound')" class="help text-danger">
                {{$t(formErrors.get('sound'))}}
              </small>
            </div>

          </div>
          <!-- --- --->
        </div>

      </form>
    </div>
  </div>

</template>

<script>

import Errors from "@/utils/StandardErrors";
import Multiselect from 'vue-multiselect'
import * as countries from "i18n-iso-countries";
import Vue from "vue";

export default {
  components: {
    Multiselect,
  },
  props: {
    value: {
      type: Object,
      required: false
    },
    formErrors: {
      type: Object,
      required: false,
      default: () => {
        return new Errors()
      }
    },
    clientOptions: {
      type: Array,
      required: true,
    },
    collapsable: {
      type: Boolean,
      default: false
    },
    collapsed: {
      type: Boolean,
      default: false
    },
  },
  data: function () {
    return {
      client: {},
      isCollapsed: this.collapsable && this.collapsed,
      langOptions: this.$store.state.languages,
      allowedAudioExtensions: ['mp3'],
    }
  },
  mounted() {
    if(this.value && this.value.client_id) {
      this.value.client = this.clientOptions.find(elem => elem.id === parseInt(this.value.client_id))
      this.client = this.clientOptions.find(elem => elem.id === parseInt(this.value.client_id))
    }
  },
  methods: {
    handleImgSrc: function () {
      return typeof this.value.icon === 'string' ? this.value.icon : URL.createObjectURL(this.value.icon)
    },
    playSound: function() {
      new Audio(this.value.sound).play()
    },
    onIconFileSelected: function (event) {
      let files = event.target.files || event.dataTransfer.files
      if (!files.length) {
        return
      }
      this.value.icon = files[0]
    },
    onSoundFileSelected: function (event) {
      let files = event.target.files || event.dataTransfer.files
      if (!files.length) {
        return
      }
      this.value.sound = files[0]
    },
    uploadSound: function () {
      //TODO
      this.$refs.uploadSoundFileInput.click()
    },
    uploadImage: function () {
      //TODO
      this.$refs.uploadIconFileInput.click()
    },
    removeSound: function () {
      this.value.sound = null
    },
    removeIcon: function () {
      this.value.icon = null
    }
  },
  computed: {
    /*countries: function () {
      for (let code of this.$store.state.countries.allowed) {
        if (code.toLowerCase() === "gb") {
          code = "en"
        }
        countries.registerLocale(require(`i18n-iso-countries/langs/${code.toLowerCase()}.json`))
      }
      let tempCountries = countries.getNames(Vue.i18n.locale().toLowerCase() === 'gb' ? 'en' : Vue.i18n.locale().toLowerCase())
      let countriesList = []
      for(const key in tempCountries) {
        countriesList.push({
          name: tempCountries[key],
          code: key
        })
      }
      return countriesList
      //return CountryList.filter(country => this.$store.state.countries.allowed.includes(country.code))
      //return CountryList
    },*/
    countries: function () {
      for (let code of this.$store.state.countries.allowed) {
        if (code.toLowerCase() === "gb") {
          code = "en"
        } else if (code.toLowerCase() === 'cz') {
          code = 'cs'
        }
        countries.registerLocale(require(`i18n-iso-countries/langs/${code.toLowerCase()}.json`))
      }
      let vueI18nLocale = Vue.i18n.locale().toLowerCase()
      switch (vueI18nLocale) {
        case 'gb':
          vueI18nLocale = 'en'
          break;
        case 'cz':
          vueI18nLocale = 'cs'
          break;
      }
      let tempCountries = countries.getNames(vueI18nLocale)
      let countriesList = []
      for(const key in tempCountries) {
        countriesList.push({
          name: tempCountries[key],
          code: key
        })
      }
      return countriesList
      //return CountryList.filter(country => this.$store.state.countries.allowed.includes(country.code))
      //return CountryList
    },
  },
  watch: {
    client: function (val) {
      if (val) {
        this.value.client = val
      }
    }
  }
}
</script>

<style scoped>
.evollu-form .row {
  margin: 0 0 1.25rem !important;
}
.add-icon-wh{
  width: 2.5rem;
  height: 2.5rem;
}
.verical-line{
  width: 3px;
  height: 33px;
  background: #8E99B4;
  border-radius: 1.5px;
  align-self: center;
  margin: 0 10px;
  display: flex;
}
.custom-border-outline {
  border: 2px solid rgba(65, 65, 65, 0.1);
  border-radius: 25px;
}

.upload-div {
  display: flex;
  font-size: 12px;
  background: #EAEAEA;
  border: 2px dashed rgba(65, 65, 65, 0.1);
  border-radius: 25px;
}

</style>
